import { useState } from 'react';
import { useNavigate, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faClipboardList, faChevronRight, faFilter } from '@fortawesome/free-solid-svg-icons';
import { customerJobService } from '@/services/customer-job.service';
import { customerJobDetailPath } from '@/routes/customer-portal-routes';
import { useCustomerAuthStore } from '@/stores/customer-auth.store';
import { locationParamFor, useCustomerScopeStore } from '@/stores/customer-scope.store';
import { CustomerLocationContext } from '@/components/customer-portal/CustomerLocationContext';
import { CustomerSearchBar } from '@/components/customer-portal/CustomerSearchBar';
import { CustomerPagination } from '@/components/customer-portal/CustomerPagination';
import { CustomerPageHeader } from '@/components/customer-portal/CustomerPageHeader';
import { CustomerEmptyState } from '@/components/customer-portal/CustomerEmptyState';
import { CustomerErrorState } from '@/components/customer-portal/CustomerErrorState';
import { CustomerStatusBadge } from '@/components/customer-portal/CustomerStatusBadge';
import { CustomerSkeletonList } from '@/components/customer-portal/skeletons/CustomerSkeleton';
import { useDebounce } from '@/hooks/use-debounce';
import { JOB_STATUS_TONE } from './customer-job-status-tone';

const PAGE_SIZE = 20;

function fmtDate(iso: string): string {
  return new Date(iso).toLocaleDateString(undefined, {
    year: 'numeric',
    month: 'short',
    day: 'numeric',
  });
}

export function CustomerJobListPage() {
  const navigate = useNavigate();
  const user = useCustomerAuthStore((s) => s.user);
  const selectedLocation = useCustomerScopeStore((s) => s.selectedLocationId);
  // D-130 — a LOCATION contact never sends a locationId: the server already
  // confines it to its own site (see locationParamFor).
  const locationId = locationParamFor(user?.locationScope, selectedLocation);
  const [searchParams, setSearchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [search, setSearch] = useState('');
  const debouncedSearch = useDebounce(search, 350);
  const actionRequiredOnly = searchParams.get('actionRequired') === 'true';

  const { data, isLoading, isError, refetch } = useQuery({
    queryKey: [
      'customer-portal',
      'jobs',
      page,
      debouncedSearch,
      actionRequiredOnly,
      locationId ?? 'all',
    ],
    queryFn: () =>
      customerJobService.list({
        page,
        pageSize: PAGE_SIZE,
        search: debouncedSearch || undefined,
        actionRequired: actionRequiredOnly || undefined,
        locationId,
      }),
  });

  const toggleActionRequired = () => {
    const next = new URLSearchParams(searchParams);
    if (actionRequiredOnly) next.delete('actionRequired');
    else next.set('actionRequired', 'true');
    setSearchParams(next);
    setPage(1);
  };

  return (
    <div className="space-y-4 sm:space-y-5">
      <CustomerPageHeader
        title="Jobs"
        subtitle="Service jobs for your equipment"
        eyebrow={data ? `${data.total} job${data.total === 1 ? '' : 's'}` : undefined}
      />

      <div className="flex flex-col gap-2 sm:flex-row sm:items-center">
        <CustomerSearchBar
          value={search}
          onChange={(v) => {
            setSearch(v);
            setPage(1);
          }}
          placeholder="Search by job number or equipment…"
        />
        <CustomerLocationContext className="sm:w-52" />
        <button
          onClick={toggleActionRequired}
          aria-pressed={actionRequiredOnly}
          className={`customer-pressable inline-flex min-h-11 items-center justify-center gap-2 rounded-customer-sm border px-3.5 text-sm font-medium transition-colors focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-customer-ring ${
            actionRequiredOnly
              ? 'border-customer-warning bg-customer-warning/10 text-customer-warning-text'
              : 'border-customer-border bg-customer-surface text-customer-text/80 hover:border-customer-text/30 hover:bg-customer-surface-muted'
          }`}
        >
          <FontAwesomeIcon icon={faFilter} className="h-3.5 w-3.5" aria-hidden="true" />
          Action required only
        </button>
      </div>

      {isLoading && <CustomerSkeletonList rows={6} />}

      {isError && !isLoading && (
        <CustomerErrorState message="We couldn't load your jobs." onRetry={() => void refetch()} />
      )}

      {data && !isLoading && !isError && (
        <>
          {data.items.length === 0 ? (
            <div className="rounded-customer border border-dashed border-customer-border bg-customer-surface">
              <CustomerEmptyState
                icon={faClipboardList}
                tone="neutral"
                title="No jobs found"
                description={
                  search || actionRequiredOnly
                    ? 'No jobs match your current search or filter.'
                    : 'No service jobs are recorded for your account yet.'
                }
                className="py-14"
              />
            </div>
          ) : (
            <div className="customer-fade-in overflow-hidden rounded-customer border border-customer-border bg-customer-surface shadow-customer-sm">
              <ul className="divide-y divide-customer-border">
                {data.items.map((item) => (
                  <li key={item.id}>
                    <button
                      onClick={() => navigate(customerJobDetailPath(item.id))}
                      className={`customer-pressable flex w-full items-center gap-3 px-4 py-3.5 text-left transition-colors hover:bg-customer-surface-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-customer-ring sm:px-5 ${
                        item.actionRequired ? 'border-l-[3px] border-l-customer-warning' : ''
                      }`}
                    >
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-x-2 gap-y-1">
                          <p className="text-sm font-semibold text-customer-text">
                            {item.inspectionNumber}
                          </p>
                          <CustomerStatusBadge
                            label={item.customerFacingStatus}
                            tone={JOB_STATUS_TONE[item.customerFacingStatus]}
                          />
                          {item.actionRequired && (
                            <CustomerStatusBadge label="Action Required" tone="warning" icon />
                          )}
                        </div>
                        <p className="mt-1 truncate text-sm text-customer-text/80">
                          {item.equipmentName}
                        </p>
                        <p className="mt-0.5 truncate text-xs text-customer-text-secondary">
                          Asset {item.equipmentAssetNumber}
                          {item.receivedAt && ` · Received ${fmtDate(item.receivedAt)}`}
                        </p>
                      </div>
                      <FontAwesomeIcon
                        icon={faChevronRight}
                        className="h-3.5 w-3.5 shrink-0 text-customer-text-secondary"
                        aria-hidden="true"
                      />
                    </button>
                  </li>
                ))}
              </ul>
            </div>
          )}

          <CustomerPagination
            page={page}
            pageSize={PAGE_SIZE}
            total={data.total}
            onPageChange={setPage}
          />
        </>
      )}
    </div>
  );
}
